/**
 * Workspace API keys for the TDIA GitHub Action (DEC-201, UI_SDD §R.15). Admins only.
 * A new key is shown once, with Copy; afterwards only its prefix is visible.
 */
import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { apiKeysApi, type ApiKey } from '../../api/githubApp'
import { Button } from '../shared/Button'

function day(iso: string | null): string {
  return iso ? new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : 'Never'
}

export function ApiKeysCard() {
  const qc = useQueryClient()
  const { data } = useQuery({ queryKey: ['api-keys'], queryFn: apiKeysApi.list, retry: false })
  const [name, setName] = useState('')
  const [created, setCreated] = useState<ApiKey | null>(null)
  const [copied, setCopied] = useState(false)
  const [confirming, setConfirming] = useState<string | null>(null)

  const create = useMutation({
    mutationFn: () => apiKeysApi.create(name.trim()),
    onSuccess: (key) => {
      setCreated(key)
      setCopied(false)
      setName('')
      void qc.invalidateQueries({ queryKey: ['api-keys'] })
    },
  })
  const revoke = useMutation({
    mutationFn: (id: string) => apiKeysApi.revoke(id),
    onSuccess: () => {
      setConfirming(null)
      void qc.invalidateQueries({ queryKey: ['api-keys'] })
    },
  })

  async function copy() {
    if (!created?.key) return
    try {
      await navigator.clipboard.writeText(created.key)
      setCopied(true)
    } catch {
      setCopied(false)
    }
  }

  const keys = data?.keys ?? []
  return (
    <div className="bg-panel border border-hairline rounded-md overflow-hidden mt-4" data-testid="api-keys-card">
      <div className="px-4 py-3 border-b border-fill-subtle font-semibold text-[10px] tracking-[2px] uppercase text-muted">
        API keys
      </div>
      <div className="px-4 py-[14px] flex flex-col gap-3">
        <p className="text-[12px] text-muted m-0">
          The TDIA GitHub Action uses a key to send your code to this workspace for scanning.
        </p>

        {created?.key && (
          <div className="rounded-md border border-hairline-strong bg-ground px-3 py-2" data-testid="api-key-once">
            <div className="text-[12px] font-semibold text-ink mb-1">{created.name}</div>
            <code className="block text-[12px] text-body break-all">{created.key}</code>
            <div className="flex items-center gap-3 mt-2">
              <Button size="sm" variant="secondary" onClick={() => void copy()}>
                {copied ? 'Copied' : 'Copy'}
              </Button>
              <span className="text-[11px] text-muted">You will not see it again.</span>
            </div>
          </div>
        )}

        <form
          className="flex items-center gap-2"
          onSubmit={(e) => {
            e.preventDefault()
            if (name.trim()) create.mutate()
          }}
        >
          <input
            id="api-key-name"
            aria-label="Key name"
            placeholder="Key name, for example CI"
            value={name}
            onChange={(e) => setName(e.target.value)}
            maxLength={100}
            className="flex-1 border border-control rounded-md px-3 py-2 text-[13px] focus:outline-none focus-visible:shadow-focus"
          />
          <Button type="submit" size="sm" loading={create.isPending} disabled={!name.trim()}>
            Create key
          </Button>
        </form>

        {keys.length > 0 && (
          <table className="w-full text-[13px]" data-testid="api-keys-table">
            <thead>
              <tr className="text-left text-[11px] text-muted">
                <th scope="col" className="font-medium pb-1">Name</th>
                <th scope="col" className="font-medium pb-1">Key</th>
                <th scope="col" className="font-medium pb-1">Created</th>
                <th scope="col" className="font-medium pb-1">Last used</th>
                <th scope="col" className="pb-1" />
              </tr>
            </thead>
            <tbody>
              {keys.map((k) => (
                <tr key={k.key_id} className="border-t border-hairline">
                  <td className="py-2 text-ink">{k.name}</td>
                  <td className="py-2 text-muted tabular-nums">{k.prefix}...</td>
                  <td className="py-2 text-muted">{day(k.created_at)}</td>
                  <td className="py-2 text-muted">{day(k.last_used_at)}</td>
                  <td className="py-2 text-right">
                    {k.revoked_at ? (
                      <span className="text-[12px] text-faint">Revoked</span>
                    ) : confirming === k.key_id ? (
                      <span className="inline-flex items-center gap-2" role="alertdialog" aria-label="Confirm revoke">
                        <span className="text-[12px] text-body">Revoke this key?</span>
                        <Button size="sm" variant="secondary" loading={revoke.isPending} onClick={() => revoke.mutate(k.key_id)}>
                          Revoke
                        </Button>
                        <Button size="sm" variant="ghost" onClick={() => setConfirming(null)}>
                          Cancel
                        </Button>
                      </span>
                    ) : (
                      <Button size="sm" variant="ghost" onClick={() => setConfirming(k.key_id)}>
                        Revoke
                      </Button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  )
}
